import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Mail, RefreshCw, RotateCcw, Send } from "lucide-react";

const f = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Stat({ label, value, warn, testid }) {
  return (
    <Card className={`p-3 ${warn ? "border-rose-200 bg-rose-50" : ""}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${warn ? "text-rose-700" : "text-black"}`}>{value}</div>
    </Card>
  );
}

// Section 8: the health page — what the connector's get_email_agent_health returns, with the retry / sweep / renew doors.
export function HealthPanel({ canRun }) {
  const [h, setH] = useState(null);
  const load = () => api.get("/bd/health").then((r) => setH(r.data)).catch(() => setH(null));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const act = async (path, ok) => { try { const r = await api.post(path, {}); toast.success(ok || r.data.message || "Done"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  if (!h) return <div className="text-sm text-slate-400 py-12 text-center">Loading…</div>;
  const acc = h.accuracy_7d || {};
  return (
    <div className="space-y-4" data-testid="bd-health">
      {h.sweep_stale && (
        <div className="rounded-md border border-rose-300 bg-rose-50 text-rose-800 px-4 py-3 text-sm font-medium" data-testid="bd-health-stale-banner">
          GridForge has not checked your email since {f(h.stale_since)}. Press “Sweep now”; if it stays red, the mailbox connection or the server needs a look.
        </div>
      )}
      <div className="text-xs text-slate-500 flex flex-wrap gap-x-4" data-testid="bd-health-triggers">
        <span>Last sweep run {f(h.last_run_at)}{h.last_run_source ? ` via ${h.last_run_source}` : ""}</span>
        <span>Platform cron last hit {f(h.last_cron_hit_at)}</span>
        <span>In-app scheduler last tick {f(h.last_scheduler_tick_at)}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat label="Agent" value={h.enabled ? `On · ${h.mode}` : "Off (kill switch)"} warn={!h.enabled} testid="bd-health-enabled" />
        <Stat label="Last email received" value={f(h.last_email_received)} testid="bd-health-received" />
        <Stat label="Last email judged" value={f(h.last_email_judged)} testid="bd-health-judged" />
        <Stat label="Waiting" value={h.waiting} warn={h.waiting > 20} testid="bd-health-waiting" />
        <Stat label="Could not judge" value={h.could_not_judge?.length || 0} warn={(h.could_not_judge?.length || 0) > 0} testid="bd-health-failed" />
        <Stat label="Failures 24h" value={h.failures_24h?.length || 0} warn={(h.failures_24h?.length || 0) > 3} testid="bd-health-failures" />
        <Stat label="7-day accuracy" value={acc.accuracy != null ? `${Math.round(acc.accuracy * 100)}% (${acc.judgments} judged, ${acc.corrections} corrected)` : `${acc.judgments || 0} judged`} testid="bd-health-accuracy" />
      </div>
      <Card className="p-4" data-testid="bd-health-mailboxes">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-[#003E87]" /> Mailboxes</div>
          {canRun && <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => act("/bd/sweep-now")} data-testid="bd-health-sweep"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Sweep now</Button>
            <Button size="sm" variant="outline" onClick={() => act("/bd/watch/renew", "Gmail watch renewed")} disabled={!h.push_configured} title={h.push_configured ? "" : "GMAIL_PUBSUB_TOPIC is not set"} data-testid="bd-health-renew"><Activity className="w-3.5 h-3.5 mr-1" /> Renew watch</Button>
            <Button size="sm" variant="outline" onClick={() => act("/bd/morning-summary/send", "Summary sent (if there was anything to report)")} data-testid="bd-health-summary"><Send className="w-3.5 h-3.5 mr-1" /> Send 7am summary now</Button>
          </div>}
        </div>
        <div className="mt-2 space-y-1 text-xs text-slate-600">
          {(h.mailboxes || []).length === 0 && <div data-testid="bd-health-no-mailbox">No Gmail mailbox connected.</div>}
          {(h.mailboxes || []).map((m) => (
            <div key={m.email} className={`flex flex-wrap gap-x-4 ${m.error ? "text-rose-700" : ""}`} data-testid={`bd-health-mailbox-${m.email}`}>
              <span className="font-medium">{m.email}</span>
              <span>Push: {h.push_configured ? (m.watch_expiry ? `watch expires ${f(m.watch_expiry)}` : "watch not set") : "not configured (topic missing) — 15-min sweep only"}</span>
              <span>Last sweep {f(m.last_sweep_at)}{m.last_drain_at ? ` · drained ${f(m.last_drain_at)}` : ""}</span>
              {m.last_push_at && <span>Last push {f(m.last_push_at)}</span>}
              {m.error && <span>Error: {m.error}</span>}
            </div>
          ))}
        </div>
      </Card>
      {(h.could_not_judge || []).length > 0 && (
        <Card className="p-4 border-rose-200" data-testid="bd-health-could-not-judge">
          <div className="flex items-center justify-between"><div className="text-sm font-semibold text-rose-800">Could not judge (after 3 tries)</div>
            {canRun && <Button size="sm" variant="outline" onClick={() => act("/bd/queue/retry", "Re-queued")} data-testid="bd-health-retry-all"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Retry all</Button>}</div>
          <ul className="mt-2 space-y-1 text-xs">{h.could_not_judge.map((q) => <li key={q.queue_id} data-testid={`bd-health-fail-${q.queue_id}`}><span className="text-slate-500">{f(q.last_try_at)}</span> · {q.mailbox} · {q.gmail_msg_id} — {q.error}</li>)}</ul>
        </Card>
      )}
      {(h.alerts_recent || []).length > 0 && (
        <Card className="p-4" data-testid="bd-health-alerts">
          <div className="text-sm font-semibold">Alerts sent to Taylor</div>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">{h.alerts_recent.map((a) => <li key={a.alert_id}>{f(a.sent_at)} · {a.kind} · {a.subject || "morning summary"} {a.sent ? "" : "(send failed)"}</li>)}</ul>
        </Card>
      )}
    </div>
  );
}
